/**
 * Settings → Staff Roles — ADMIN ONLY (App.tsx route is roles=['admin']).
 *
 * Owner-asked before 26 May: an in-portal way to assign AVBP roles per
 * staff member so the owner can grant Khilendra a leads-capable / admin
 * role WITHOUT hand-editing Firestore (lead-identity edit is admin-only).
 *
 * Mirrors HR's Settings → Role Management mental model (a role list, an
 * edit affordance, a permission preview) but scoped to AVBP's real,
 * currently-coarse login roles. Writes staff/{id}.roles via the pure,
 * unit-tested staffRoles seam. Reuses the Clients-screen UI pattern
 * (Box / Card / Btn3 / inline edit panel). Additive only — no shell or
 * route re-architecture.
 *
 * PROPAGATION (surfaced in-UI, never silently misleading): claims are
 * minted from staff/ at the staffer's NEXT sign-in
 * (resolveTeamClaimsOnSignIn + forced token refresh) — a role change here
 * is live on disk immediately but takes effect for that person on their
 * next login / token refresh.
 */
import { useEffect, useState } from 'react';
import { useAuth } from '../lib/auth';
import { useToast } from '../components/Toast';
import { Box, Btn3, Card, EmptyNote, Tag } from '../components/ui';
import {
  listenAvbpStaff,
  updateStaffRoles,
  permissionsForRoles,
  ROLE_OPTIONS,
  AVBP_PERMISSIONS,
  type AvbpStaffRow,
} from '../lib/staffRoles';

const inputStyle: React.CSSProperties = {
  border: '1px solid var(--rule)',
  borderRadius: 'var(--r-sm)',
  padding: '8px 10px',
  fontSize: 13.5,
  fontFamily: 'inherit',
  color: 'var(--ink)',
  background: 'var(--surface)',
  width: '100%',
};

function Lbl({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 12,
        fontWeight: 600,
        color: 'var(--ink-mute)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        margin: '10px 0 6px',
      }}
    >
      {children}
    </div>
  );
}

export default function SettingsStaffRoles() {
  const { user } = useAuth();
  const { toast } = useToast();
  const actorName =
    user?.displayName || user?.email?.split('@')[0] || 'Admin';

  const [rows, setRows] = useState<AvbpStaffRow[] | null>(null);
  const [editing, setEditing] = useState<string | null>(null);

  useEffect(() => {
    const unsub = listenAvbpStaff(
      setRows,
      () => toast('Could not load staff directory (check connection)'),
    );
    return unsub;
  }, [toast]);

  return (
    <Box
      title="Staff roles"
      note="Admin only — assign each staffer's AVBP role"
    >
      <p
        style={{
          fontSize: 13,
          color: 'var(--ink-soft)',
          marginBottom: 14,
          lineHeight: 1.5,
        }}
      >
        Roles are read from the staff directory when a person signs in.
        Changing a role here is saved immediately, but{' '}
        <b>takes effect for that person on their next sign-in</b> (their
        access token is re-minted from this directory at login). Ask them to
        sign out and back in if the change must apply now.
      </p>

      {rows === null && (
        <EmptyNote title="Loading staff…">
          Reading the AVBP staff directory.
        </EmptyNote>
      )}

      {rows !== null && rows.length === 0 && (
        <EmptyNote title="No AVBP staff found">
          No staff records carry the <code>avbp</code> surface. Add the
          surface in the HR app first, then they appear here.
        </EmptyNote>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {(rows ?? []).map((s) => {
          const perms = permissionsForRoles(s.roles);
          const isOpen = editing === s.id;
          return (
            <Card key={s.id} style={s.active ? undefined : { opacity: 0.6 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  flexWrap: 'wrap',
                }}
              >
                <b>{s.name}</b>
                <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
                  {s.id} · {s.email}
                </span>
                {s.roles.length === 0 ? (
                  <Tag kind="warn">no role</Tag>
                ) : (
                  s.roles.map((r) => (
                    <Tag key={r} kind="val">
                      {r}
                    </Tag>
                  ))
                )}
                {!s.active && <Tag kind="warn">inactive</Tag>}
                <span
                  style={{
                    fontSize: 12,
                    color: 'var(--ink-mute)',
                  }}
                >
                  {perms.size} AVBP capabilities
                </span>
                <span style={{ marginLeft: 'auto' }}>
                  <Btn3
                    intent="ghost"
                    size="sm"
                    onClick={() =>
                      setEditing((e) => (e === s.id ? null : s.id))
                    }
                  >
                    {isOpen ? 'Close' : 'Change role'}
                  </Btn3>
                </span>
              </div>
              {isOpen && (
                <RoleEditPanel
                  staff={s}
                  actorName={actorName}
                  onMsg={(m) => toast(m)}
                  onDone={() => setEditing(null)}
                />
              )}
            </Card>
          );
        })}
      </div>
    </Box>
  );
}

function RoleEditPanel({
  staff,
  actorName,
  onMsg,
  onDone,
}: {
  staff: AvbpStaffRow;
  actorName: string;
  onMsg: (m: string) => void;
  onDone: () => void;
}) {
  // One primary role (the unit roleFromClaims actually resolves on).
  const current =
    staff.roles.find((r) => r.toLowerCase() === 'admin') ||
    staff.roles[0] ||
    'staff';
  const [role, setRole] = useState(current.toLowerCase());
  const [busy, setBusy] = useState(false);

  const previewPerms = permissionsForRoles([role]);

  async function save() {
    if (busy) return;
    setBusy(true);
    try {
      await updateStaffRoles(staff.id, [role], actorName);
      onMsg(
        `${staff.name} → "${role}". Applies on their next sign-in.`,
      );
      onDone();
    } catch (e) {
      onMsg(`Save failed: ${(e as Error).message}`);
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        marginTop: 14,
        paddingTop: 14,
        borderTop: '1px solid var(--rule)',
      }}
    >
      <Lbl>AVBP role</Lbl>
      <select
        value={role}
        onChange={(e) => setRole(e.target.value)}
        style={{ ...inputStyle, minWidth: 240 }}
        disabled={busy}
      >
        {ROLE_OPTIONS.map((o) => (
          <option key={o.role} value={o.role}>
            {o.label}
          </option>
        ))}
      </select>
      <p
        style={{
          fontSize: 12.5,
          color: 'var(--ink-soft)',
          marginTop: 8,
          lineHeight: 1.5,
        }}
      >
        {ROLE_OPTIONS.find((o) => o.role === role)?.blurb}
      </p>

      <Lbl>What this role can do in AVBP today</Lbl>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {AVBP_PERMISSIONS.map((p) => {
          const on = previewPerms.has(p.key);
          return (
            <div
              key={p.key}
              style={{
                fontSize: 12.5,
                color: on ? 'var(--ink)' : 'var(--ink-mute)',
                display: 'flex',
                gap: 8,
              }}
            >
              <span
                aria-hidden
                style={{ color: on ? 'var(--good)' : 'var(--ink-mute)' }}
              >
                {on ? '✓' : '·'}
              </span>
              <span>
                {p.label}{' '}
                <span style={{ color: 'var(--ink-mute)' }}>({p.group})</span>
              </span>
            </div>
          );
        })}
        {previewPerms.size === 0 && (
          <p style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
            This role has no built AVBP track yet (HR / Partner / External
            consultant land on a reserved placeholder until their sprint).
          </p>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <Btn3
          intent="grn"
          size="sm"
          onClick={save}
          disabled={busy || role === current.toLowerCase()}
        >
          {busy ? 'Saving…' : 'Save role'}
        </Btn3>
        <Btn3 intent="ghost" size="sm" onClick={onDone} disabled={busy}>
          Cancel
        </Btn3>
      </div>
    </div>
  );
}
